import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2, ArrowRight, Trash2, CheckCircle2, AlertTriangle, LayoutGrid, List, Search, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { fmtMoney, fmtNum, pnlColor } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import StatusPill from "../components/StatusPill";

export default function BackToBack() {
  const qc = useQueryClient();
  const { data: pcs = [] } = useQuery({ queryKey: ["purchase-contracts"], queryFn: () => api.get("/purchase-contracts") });
  const { data: scs = [] } = useQuery({ queryKey: ["sale-contracts"], queryFn: () => api.get("/sale-contracts") });
  const { data: links = [] } = useQuery({ queryKey: ["contract-links"], queryFn: () => api.get("/contract-links") });

  const [selPc, setSelPc] = useState(null);
  const [selSc, setSelSc] = useState(null);
  const [qty, setQty] = useState("");
  const [linkView, setLinkView] = useState("table");
  const [linkSearch, setLinkSearch] = useState("");
  const [tileGroup, setTileGroup] = useState("purchase");
  const [expandedTile, setExpandedTile] = useState(null);

  const activePc = pcs.filter((c) => c.status !== "Cancelled");
  const activeSc = scs.filter((c) => c.status !== "Cancelled");

  const pcMap = useMemo(() => Object.fromEntries(pcs.map((c) => [c.id, c])), [pcs]);
  const scMap = useMemo(() => Object.fromEntries(scs.map((c) => [c.id, c])), [scs]);

  // coverage per purchase contract
  const pcCoverage = (id) => links.filter((l) => l.purchase_contract_id === id).reduce((s, l) => s + (l.allocated_qty || 0), 0);
  const scCoverage = (id) => links.filter((l) => l.sale_contract_id === id).reduce((s, l) => s + (l.allocated_qty || 0), 0);

  const createLink = async () => {
    if (!selPc || !selSc) return toast.error("Select one purchase and one sale contract");
    const p = pcMap[selPc], s = scMap[selSc];
    const pcRem = p.quantity - pcCoverage(selPc);
    const scRem = s.quantity - scCoverage(selSc);
    if (pcRem <= 0) return toast.error(`Purchase contract ${p.contract_ref} is already fully covered — linking not allowed`);
    if (scRem <= 0) return toast.error(`Sale contract ${s.contract_ref} is already fully covered — linking not allowed`);
    const allocated = Number(qty) || Math.min(pcRem, scRem);
    if (allocated > pcRem) return toast.error(`Allocation ${fmtNum(allocated)} MT exceeds remaining ${fmtNum(pcRem)} MT on ${p.contract_ref}`);
    if (allocated > scRem) return toast.error(`Allocation ${fmtNum(allocated)} MT exceeds remaining ${fmtNum(scRem)} MT on ${s.contract_ref}`);
    await api.post("/contract-links", {
      purchase_contract_id: selPc, sale_contract_id: selSc,
      allocated_qty: allocated, coverage_pct: 100, note: "Back-to-back allocation",
    });
    toast.success("Back-to-back link created");
    setSelPc(null); setSelSc(null); setQty("");
    qc.invalidateQueries();
  };

  const removeLink = async (id) => {
    await api.del(`/contract-links/${id}`);
    toast.success("Link removed");
    qc.invalidateQueries();
  };

  const selectedPair = selPc && selSc;
  const pcRemaining = selPc ? pcMap[selPc].quantity - pcCoverage(selPc) : 0;
  const scRemaining = selSc ? scMap[selSc].quantity - scCoverage(selSc) : 0;
  const fullyCoveredBlock = selectedPair && (pcRemaining <= 0 || scRemaining <= 0);
  const previewSpread = selectedPair
    ? (scMap[selSc].price - pcMap[selPc].price) * (Number(qty) || Math.min(pcRemaining, scRemaining) || pcMap[selPc].quantity)
    : null;

  const filteredLinks = links.filter((l) => {
    const p = pcMap[l.purchase_contract_id], s = scMap[l.sale_contract_id];
    if (!p || !s) return false;
    const q = linkSearch.trim().toLowerCase();
    return !q || [p.contract_ref, s.contract_ref, p.commodity, s.counterparty_name].some((x) => String(x || "").toLowerCase().includes(q));
  });

  const ContractCard = ({ c, side, selected, onSelect, coverage }) => {
    const cov = coverage(c.id);
    const pct = c.quantity ? Math.min(Math.round((cov / c.quantity) * 100), 100) : 0;
    const relCount = side === "pc"
      ? links.filter((l) => l.purchase_contract_id === c.id).length
      : links.filter((l) => l.sale_contract_id === c.id).length;
    const relLabel = relCount === 0 ? null : relCount > 1 ? (side === "pc" ? "1 : Many" : "Many : 1") : "1 : 1";
    return (
      <button
        data-testid={`${side}-card-${c.contract_ref}`}
        onClick={() => onSelect(selected ? null : c.id)}
        className={`w-full text-left rounded-lg border p-3 transition-all ${
          selected
            ? "border-orange-500 ring-2 ring-orange-200 dark:ring-orange-900 bg-orange-50/60 dark:bg-orange-950/30"
            : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-orange-300"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs font-bold text-orange-600 dark:text-orange-400">{c.contract_ref}</span>
          <div className="flex items-center gap-1.5">
            {relLabel && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${relCount > 1 ? "bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300" : "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300"}`}>{relLabel}</span>}
            <StatusPill status={c.status} />
          </div>
        </div>
        <p className="text-sm font-semibold uppercase mt-1">{c.commodity}</p>
        <p className="text-xs text-slate-500 truncate">{c.counterparty_name}</p>
        <div className="flex justify-between text-xs mt-2 font-mono">
          <span>{fmtNum(c.quantity)} MT</span>
          <span>{fmtMoney(c.price)}/MT</span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
          </div>
          <span className={`text-[10px] font-semibold ${pct >= 100 ? "text-emerald-600" : "text-amber-600"}`}>{pct}%</span>
        </div>
        {pct >= 100 && (
          <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
            <CheckCircle2 className="h-3 w-3" /> Fully covered{relCount > 1 ? ` · ${relCount} legs` : ""}
          </span>
        )}
      </button>
    );
  };

  // relationship type for a given link
  const relFor = (l) => {
    const pcCount = links.filter((x) => x.purchase_contract_id === l.purchase_contract_id).length;
    const scCount = links.filter((x) => x.sale_contract_id === l.sale_contract_id).length;
    if (pcCount > 1) return { label: "One → Many", tip: "One Purchase → Many Sales", cls: "bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300" };
    if (scCount > 1) return { label: "Many → One", tip: "Many Purchases → One Sale", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300" };
    return { label: "One-to-One", tip: "One-to-One", cls: "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300" };
  };

  return (
    <div className="space-y-5" data-testid="backtoback-page">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="h-5 w-5 text-orange-600" />
          <h3 className="font-bold">Back-to-Back Coverage Matrix</h3>
        </div>
        <p className="text-sm text-slate-500">Select a <b>purchase</b> contract on the left and a <b>sale</b> contract on the right, set the allocation quantity, then create the link. The coverage bar on each card shows how much is already allocated.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50/60 dark:bg-sky-950/30 p-3">
          <p className="text-xs font-bold text-sky-700 dark:text-sky-300">1 : 1 — One-to-One</p>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">One purchase fully covers one sale.</p>
          <p className="text-[11px] font-mono text-slate-500 mt-1">Eg: AGP-2026-31 (5,000) → AGS-2026-31 (5,000)</p>
        </div>
        <div className="rounded-lg border border-purple-200 dark:border-purple-900 bg-purple-50/60 dark:bg-purple-950/30 p-3">
          <p className="text-xs font-bold text-purple-700 dark:text-purple-300">1 : Many — One Purchase → Many Sales</p>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">One big purchase split across several sales.</p>
          <p className="text-[11px] font-mono text-slate-500 mt-1">Eg: AGP-2026-32 (12,000) → 32A 6k + 32B 4k + 32C 2k</p>
        </div>
        <div className="rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/30 p-3">
          <p className="text-xs font-bold text-amber-700 dark:text-amber-300">Many : 1 — Many Purchases → One Sale</p>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">One sale sourced from multiple purchases.</p>
          <p className="text-[11px] font-mono text-slate-500 mt-1">Eg: AGP-2026-33A 6k + 33B 4k → AGS-2026-33 (10,000)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 items-start">
        {/* Purchase side */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Purchase Contracts (Buy)</p>
          <div className="space-y-2 max-h-[520px] overflow-y-auto erp-scroll pr-1">
            {activePc.map((c) => <ContractCard key={c.id} c={c} side="pc" selected={selPc === c.id} onSelect={setSelPc} coverage={pcCoverage} />)}
          </div>
        </div>

        {/* Middle linker */}
        <div className="lg:sticky lg:top-24 flex flex-col items-center gap-3 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 rounded-lg p-4 min-w-[220px]">
          <p className="text-xs font-bold uppercase text-slate-500">Create Link</p>
          <div className="w-full text-center text-sm">
            <p className="font-mono text-orange-600">{selPc ? pcMap[selPc].contract_ref : "— select buy —"}</p>
            <ArrowRight className="h-4 w-4 mx-auto my-1 text-slate-400 rotate-90 lg:rotate-0" />
            <p className="font-mono text-orange-600">{selSc ? scMap[selSc].contract_ref : "— select sell —"}</p>
          </div>
          <div className="w-full">
            <label className="text-[10px] text-slate-400">Allocated Qty (MT)</label>
            <Input data-testid="b2b-qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)}
              placeholder={selPc ? String(pcMap[selPc].quantity) : "qty"} className="h-9" />
          </div>
          {selectedPair && (
            <div className={`w-full text-center rounded-md p-2 text-xs ${previewSpread >= 0 ? "bg-emerald-50 dark:bg-emerald-950/40" : "bg-rose-50 dark:bg-rose-950/40"}`}>
              <p className="text-slate-500">Locked-in spread</p>
              <p className={`font-mono font-bold ${pnlColor(previewSpread)}`}>{fmtMoney(previewSpread)}</p>
              {scMap[selSc].commodity !== pcMap[selPc].commodity && (
                <p className="flex items-center gap-1 justify-center text-amber-600 mt-1"><AlertTriangle className="h-3 w-3" /> Commodity mismatch</p>
              )}
            </div>
          )}
          {fullyCoveredBlock && (
            <div data-testid="b2b-coverage-error" className="w-full rounded-md p-2 text-xs bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{pcRemaining <= 0 ? `${pcMap[selPc].contract_ref} is fully covered.` : `${scMap[selSc].contract_ref} is fully covered.`} Linking is not allowed.</span>
            </div>
          )}
          <Button data-testid="b2b-create-link" disabled={!selectedPair || fullyCoveredBlock} onClick={createLink}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white">
            <Link2 className="h-4 w-4 mr-1" /> Link Contracts
          </Button>
        </div>

        {/* Sale side */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Sale Contracts (Sell)</p>
          <div className="space-y-2 max-h-[520px] overflow-y-auto erp-scroll pr-1">
            {activeSc.map((c) => <ContractCard key={c.id} c={c} side="sc" selected={selSc === c.id} onSelect={setSelSc} coverage={scCoverage} />)}
          </div>
        </div>
      </div>

      {/* Existing links */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <span className="font-bold">Existing Back-to-Back Links ({filteredLinks.length})</span>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="links-search" value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)}
                placeholder="Search links…" className="pl-8 h-9 w-52 bg-slate-50 dark:bg-slate-800" />
            </div>
            <div className="flex rounded-md border border-slate-200 dark:border-slate-800 overflow-hidden">
              <button data-testid="links-view-table" onClick={() => setLinkView("table")}
                className={`p-2 ${linkView === "table" ? "bg-orange-600 text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                <List className="h-4 w-4" />
              </button>
              <button data-testid="links-view-tiles" onClick={() => setLinkView("tiles")}
                className={`p-2 ${linkView === "tiles" ? "bg-orange-600 text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {filteredLinks.length === 0 && <p className="text-center py-8 text-slate-400 text-sm">No links found</p>}

        {linkView === "table" && filteredLinks.length > 0 && (
          <div className="overflow-x-auto erp-scroll">
            <table className="w-full erp-table">
              <thead className="bg-slate-100/80 dark:bg-slate-800/80">
                <tr>{["Purchase", "Commodity", "Sale", "Relationship", "Allocated (MT)", "Buy $/MT", "Sell $/MT", "Locked Spread", ""].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-left">{h}</th>))}</tr>
              </thead>
              <tbody>
                {filteredLinks.map((l) => {
                  const p = pcMap[l.purchase_contract_id], s = scMap[l.sale_contract_id];
                  const spread = (s.price - p.price) * (l.allocated_qty || 0);
                  const rel = relFor(l);
                  return (
                    <tr key={l.id} data-testid={`link-row-${l.id}`} className="border-b border-slate-100 dark:border-slate-800/60">
                      <td className="px-3 py-2 font-mono text-xs text-orange-600">{p.contract_ref}</td>
                      <td className="px-3 py-2 text-xs uppercase font-semibold">{p.commodity}</td>
                      <td className="px-3 py-2 font-mono text-xs text-orange-600">{s.contract_ref}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${rel.cls}`}>{rel.tip}</span></td>
                      <td className="px-3 py-2 text-right font-mono">{fmtNum(l.allocated_qty)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(p.price)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(s.price)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${pnlColor(spread)}`}>{fmtMoney(spread)}</td>
                      <td className="px-3 py-2 text-right">
                        <button data-testid={`unlink-${l.id}`} onClick={() => removeLink(l.id)} className="p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {linkView === "tiles" && filteredLinks.length > 0 && (() => {
          // group links by the chosen side; one tile per base contract
          const groups = {};
          filteredLinks.forEach((l) => {
            const baseId = tileGroup === "purchase" ? l.purchase_contract_id : l.sale_contract_id;
            const base = tileGroup === "purchase" ? pcMap[baseId] : scMap[baseId];
            const other = tileGroup === "purchase" ? scMap[l.sale_contract_id] : pcMap[l.purchase_contract_id];
            if (!base || !other) return;
            if (!groups[baseId]) groups[baseId] = { base, legs: [] };
            groups[baseId].legs.push({ link: l, other });
          });
          const list = Object.values(groups);
          return (
            <div>
              <div className="flex items-center gap-2 px-4 pt-3">
                <span className="text-xs text-slate-400">Group by:</span>
                {[["purchase", "Purchase Contract"], ["sale", "Sale Contract"]].map(([v, label]) => (
                  <button key={v} data-testid={`group-toggle-${v}`} onClick={() => { setTileGroup(v); setExpandedTile(null); }}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-md border ${tileGroup === v ? "bg-orange-600 text-white border-orange-600" : "border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                    {label}
                  </button>
                ))}
                <span className="text-[11px] text-slate-400 ml-1">· click a tile to see all linked contracts</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 p-4" data-testid="links-tiles">
                {list.map(({ base, legs }) => {
                  const isOpen = expandedTile === base.id;
                  const totalAlloc = legs.reduce((s, x) => s + (x.link.allocated_qty || 0), 0);
                  const totalSpread = legs.reduce((s, x) => {
                    const buyP = tileGroup === "purchase" ? base.price : x.other.price;
                    const sellP = tileGroup === "purchase" ? x.other.price : base.price;
                    return s + (sellP - buyP) * (x.link.allocated_qty || 0);
                  }, 0);
                  const pct = base.quantity ? Math.min(Math.round((totalAlloc / base.quantity) * 100), 100) : 0;
                  const relLabel = legs.length > 1 ? (tileGroup === "purchase" ? "1 : Many" : "Many : 1") : "1 : 1";
                  const relCls = legs.length > 1
                    ? (tileGroup === "purchase" ? "bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300")
                    : "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300";
                  return (
                    <div key={base.id} data-testid={`link-group-tile-${base.id}`}
                      className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden self-start">
                      <button data-testid={`group-tile-header-${base.contract_ref}`} onClick={() => setExpandedTile(isOpen ? null : base.id)}
                        className="w-full text-left p-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs font-bold text-orange-600 dark:text-orange-400">{base.contract_ref}</span>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${relCls}`}>{relLabel}</span>
                            <span className={`inline-block transition-transform text-slate-400 ${isOpen ? "rotate-90" : ""}`}><ChevronRight className="h-4 w-4" /></span>
                          </div>
                        </div>
                        <p className="text-sm font-semibold uppercase mt-1">{base.commodity}</p>
                        <p className="text-[11px] text-slate-500 truncate">{base.counterparty_name}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] font-mono text-slate-500">{fmtNum(totalAlloc)}/{fmtNum(base.quantity)} MT</span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1">
                          Linked to <b className="text-slate-600 dark:text-slate-300">{legs.length}</b> {tileGroup === "purchase" ? "sale" : "purchase"} contract{legs.length > 1 ? "s" : ""}
                        </p>
                      </button>
                      {isOpen && (
                        <div className="border-t border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800" data-testid={`group-tile-legs-${base.contract_ref}`}>
                          {legs.map(({ link, other }) => {
                            const buyP = tileGroup === "purchase" ? base.price : other.price;
                            const sellP = tileGroup === "purchase" ? other.price : base.price;
                            const spread = (sellP - buyP) * (link.allocated_qty || 0);
                            return (
                              <div key={link.id} className="px-3 py-2 flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                                    <span className="font-mono font-semibold text-orange-600 dark:text-orange-400">{other.contract_ref}</span>
                                  </div>
                                  <p className="text-[11px] text-slate-500 truncate ml-4">{other.counterparty_name}</p>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                  <div className="text-right">
                                    <p className="font-mono text-xs font-semibold">{fmtNum(link.allocated_qty)} MT</p>
                                    <p className={`font-mono text-[11px] ${pnlColor(spread)}`}>{fmtMoney(spread)}</p>
                                  </div>
                                  <button data-testid={`unlink-${link.id}`} onClick={(e) => { e.stopPropagation(); removeLink(link.id); }} className="p-1 rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600">
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                          <div className="px-3 py-2 flex items-center justify-between bg-slate-50 dark:bg-slate-800/40">
                            <span className="text-[11px] font-semibold text-slate-500">Total ({legs.length} {legs.length === 1 ? "leg" : "legs"})</span>
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-xs font-semibold">{fmtNum(totalAlloc)} MT</span>
                              <span className={`font-mono text-xs font-bold ${pnlColor(totalSpread)}`}>{fmtMoney(totalSpread)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
